// components/ARAgingReport.jsx
import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import useCompanyContext from '../hooks/useCompanyContext';
import ReportToolbar from '../shared/ReportToolbar';

const ARAgingReport = () => {
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();
  
  const [agingData, setAgingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountingClientId, setAccountingClientId] = useState(contextAccountingClientId);
  const [accountingClientName, setAccountingClientName] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  
  // Filter states
  const [agingMethod, setAgingMethod] = useState('dueDate'); // dueDate or txnDate
  const [viewType, setViewType] = useState('summary'); // summary or detail
  const [searchTerm, setSearchTerm] = useState('');
  const [includeCredits, setIncludeCredits] = useState(true);
  const [includeZero, setIncludeZero] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // ✅ NEW: Listen to company context changes via hook
  // When user switches company in header, this updates the local accountingClientId state
  // which triggers the fetch useEffect below to re-run
  useEffect(() => {
    if (contextAccountingClientId) {
      setAccountingClientId(contextAccountingClientId);
      const storedName = localStorage.getItem('accountingClientName');
      setAccountingClientName(storedName || '');
      const fy = localStorage.getItem(`activeFiscalYear:${contextAccountingClientId}`);
      if (fy) setFiscalYear(fy);
    }
  }, [contextAccountingClientId]); // Re-run when company switches

  useEffect(() => {
    const onFYChange = () => {
      if (accountingClientId) {
        const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
        if (fy) setFiscalYear(fy);
      }
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, [accountingClientId]);

  // Generate sample aging data (mock)
  const generateMockData = () => {
    const customers = [
      { id: '1', name: 'ABC Corporation', daysOverdue: 45 },
      { id: '2', name: 'XYZ Industries', daysOverdue: -5 },
      { id: '3', name: 'Global Trading', daysOverdue: 120 },
      { id: '4', name: 'Tech Solutions Inc', daysOverdue: 0 },
    ];

    const invoices = [];
    const asOfDateObj = new Date(asOfDate);

    customers.forEach(customer => {
      const count = Math.floor(Math.random() * 3) + 2;
      for (let i = 0; i < count; i++) {
        const invoiceDate = new Date(asOfDateObj);
        invoiceDate.setDate(invoiceDate.getDate() - (15 + i * 30) - customer.daysOverdue);
        const dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 30);

        const amount = (Math.random() * 50000) + 1000;
        const paid = (Math.random() > 0.4) ? (amount * (Math.random() * 0.5)) : 0;

        invoices.push({
          customerId: customer.id,
          customerName: customer.name,
          invoiceDate: invoiceDate.toISOString().split('T')[0],
          dueDate: dueDate.toISOString().split('T')[0],
          invoiceNo: `INV-${Math.floor(Math.random() * 100000)}`,
          amount,
          paid,
          openBalance: amount - paid,
        });
      }
    });

    return invoices;
  };

  const calculateAgingBucket = (dueDate, txnDate) => {
    const asOfDateObj = new Date(asOfDate);
    const referenceDate = agingMethod === 'dueDate' ? new Date(dueDate) : new Date(txnDate);
    const daysOverdue = Math.floor((asOfDateObj - referenceDate) / (1000 * 60 * 60 * 24));

    if (daysOverdue <= 0) return 'current';
    if (daysOverdue <= 30) return '1-30';
    if (daysOverdue <= 60) return '31-60';
    if (daysOverdue <= 90) return '61-90';
    return '91+';
  };

  const fetchAgingReport = async () => {
    setLoading(true);
    setError('');
    try {
      // Call the real API endpoint
      const params = new URLSearchParams({
        asOf: asOfDate,
        view: viewType,
        agingMethod: agingMethod === 'dueDate' ? 'due_date' : 'txn_date'
      });

      if (accountingClientId) {
        params.append('accountingClientId', accountingClientId);
      }
      if (fiscalYear) {
        params.append('fyId', fiscalYear);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/general-ledger/ar-aging?${params.toString()}`);
      
      if (response.data.success) {
        // Store the response data directly
        setAgingData(response.data);
      } else {
        setError(response.data.message || 'Failed to fetch AR aging data');
        setAgingData(null);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch AR aging data';
      setError(errorMsg);
      console.error('AR Aging Error:', err);
      setAgingData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountingClientId || asOfDate) {
      fetchAgingReport();
    }
  }, [accountingClientId, asOfDate, agingMethod, viewType]);

  // Re-fetch when search changes (with debounce would be better, but keep it simple)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (accountingClientId || asOfDate) {
        fetchAgingReport();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);

  const formatCurrencyCSV = (amount) =>
    parseFloat((amount || 0).toFixed(2));

  // Extract summary and totals from API response
  const processData = () => {
    if (!agingData || !agingData.summary) {
      return { summary: [], totals: { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '91+': 0, total: 0 } };
    }

    // API already returns properly structured data
    let summary = agingData.summary || [];
    const totals = agingData.totals || { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '91+': 0, total: 0 };

    // Client-side filtering for include/exclude options
    if (!includeZero) {
      summary = summary.filter(cust => cust.buckets.total !== 0);
    }
    
    return { summary, totals };
  };

  const { summary, totals } = processData();

  // CSV Export
  const exportToCSV = () => {
    if (!summary || summary.length === 0) {
      alert('No data to export');
      return;
    }

    const rows = [
      ['AR Aging Summary', `As of: ${asOfDate}`, `Client: ${accountingClientName}`],
      [],
      ['Customer', 'Current', '1-30', '31-60', '61-90', '91+', 'Total'],
      ...summary.map(cust => [
        cust.customerName,
        formatCurrencyCSV(cust.buckets.current),
        formatCurrencyCSV(cust.buckets['1-30']),
        formatCurrencyCSV(cust.buckets['31-60']),
        formatCurrencyCSV(cust.buckets['61-90']),
        formatCurrencyCSV(cust.buckets['91+']),
        formatCurrencyCSV(cust.buckets.total),
      ]),
      [],
      [
        'TOTAL',
        formatCurrencyCSV(totals.current),
        formatCurrencyCSV(totals['1-30']),
        formatCurrencyCSV(totals['31-60']),
        formatCurrencyCSV(totals['61-90']),
        formatCurrencyCSV(totals['91+']),
        formatCurrencyCSV(totals.total),
      ],
    ];

    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ar-aging-${asOfDate}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print
  const handlePrint = () => {
    if (!summary || summary.length === 0) {
      alert('No data to print');
      return;
    }

    const fmt = (amt) => formatCurrency(amt);

    const rows = summary.map(cust => `
      <tr>
        <td style="padding:4px 8px;">${cust.customerName}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(cust.buckets.current)}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(cust.buckets['1-30'])}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(cust.buckets['31-60'])}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(cust.buckets['61-90'])}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;">${fmt(cust.buckets['91+'])}</td>
        <td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:bold;">${fmt(cust.buckets.total)}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>AR Aging</title>
  <style>
    body { font-family: 'Courier New', monospace; font-size: 12px; color: #111; margin: 32px; }
    .header { text-align: center; margin-bottom: 24px; }
    .header h1 { font-size: 18px; margin: 0; font-weight: bold; }
    .header .sub { font-size: 11px; color: #666; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; }
    th { padding: 6px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 2px solid #d1d5db; background: #f3f4f6; }
    th.r { text-align: right; }
    td { padding: 4px 8px; border-bottom: 1px solid #e5e7eb; }
    .total-row { font-weight: bold; background: #f3f4f6; border-top: 2px solid #d1d5db; border-bottom: 2px solid #d1d5db; }
    .toolbar { text-align: center; margin-bottom: 16px; }
    .toolbar button { padding: 8px 20px; font-size: 12px; cursor: pointer; background: #0d9488; color: #fff; border: none; border-radius: 4px; }
    @media print { .toolbar { display: none; } @page { margin: 15mm; } }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">🖨 Print / Save as PDF</button>
  </div>
  
  <div class="header">
    ${accountingClientName ? `<div style="font-size:16px;font-weight:bold;">${accountingClientName}</div>` : ''}
    <h1>AR Aging Summary</h1>
    <div class="sub">As of: ${asOfDate}${fiscalYear ? ` | Fiscal Year: ${fiscalYear}` : ''}</div>
  </div>
  
  <table>
    <thead>
      <tr>
        <th>Customer</th>
        <th class="r">Current</th>
        <th class="r">1-30</th>
        <th class="r">31-60</th>
        <th class="r">61-90</th>
        <th class="r">91+</th>
        <th class="r">Total</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr class="total-row">
        <td>TOTAL</td>
        <td style="text-align:right;">${fmt(totals.current)}</td>
        <td style="text-align:right;">${fmt(totals['1-30'])}</td>
        <td style="text-align:right;">${fmt(totals['31-60'])}</td>
        <td style="text-align:right;">${fmt(totals['61-90'])}</td>
        <td style="text-align:right;">${fmt(totals['91+'])}</td>
        <td style="text-align:right;">${fmt(totals.total)}</td>
      </tr>
    </tbody>
  </table>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1000,height=800');
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  };

  const toggleCustomerDetail = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Controls */}
        <div className="no-print mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">AR AGING SUMMARY</h1>

          <div className="flex justify-center mb-6">
            <ReportToolbar
              showPeriod={false}
              onExportCSV={summary?.length ? exportToCSV : undefined}
              onPrint={summary?.length ? handlePrint : undefined}
              onRefresh={fetchAgingReport}
              refreshing={loading}
            />
          </div>

          {/* Filter Bar */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            {/* Row 1: Date and View Type */}
            <div className="grid grid-cols-2 gap-4 mb-4 md:grid-cols-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">As of Date:</label>
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-400 rounded text-sm"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Aging Method:</label>
                <select
                  value={agingMethod}
                  onChange={(e) => setAgingMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-400 rounded text-sm"
                >
                  <option value="dueDate">Based on Due Date</option>
                  <option value="txnDate">Based on Transaction Date</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">View:</label>
                <select
                  value={viewType}
                  onChange={(e) => setViewType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-400 rounded text-sm"
                >
                  <option value="summary">Summary</option>
                  <option value="detail">Detail</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Search:</label>
                <input
                  type="text"
                  placeholder="Customer name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-400 rounded text-sm"
                />
              </div>
            </div>

            {/* Row 2: Toggles and Buttons */}
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeCredits}
                  onChange={(e) => setIncludeCredits(e.target.checked)}
                  className="rounded"
                />
                <span>Include Credits</span>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeZero}
                  onChange={(e) => setIncludeZero(e.target.checked)}
                  className="rounded"
                />
                <span>Include Zero Balances</span>
              </label>

              {fiscalYear && (
                <div className="text-sm text-gray-500 font-medium ml-auto">
                  FY: <span className="text-gray-900">{fiscalYear}</span>
                </div>
              )}
            </div>

            {accountingClientName && (
              <div className="text-sm text-gray-600 font-medium mt-3">{accountingClientName}</div>
            )}
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Content */}
        {!loading && summary && summary.length > 0 && (
          <div className="bg-white p-8 rounded-lg shadow-sm font-mono text-sm">
            {viewType === 'summary' ? (
              <>
                {/* Summary View */}
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-900">
                        <th className="text-left px-4 py-2">Customer</th>
                        <th className="text-right px-4 py-2">Current</th>
                        <th className="text-right px-4 py-2">1-30</th>
                        <th className="text-right px-4 py-2">31-60</th>
                        <th className="text-right px-4 py-2">61-90</th>
                        <th className="text-right px-4 py-2">91+</th>
                        <th className="text-right px-4 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((cust, idx) => (
                        <tr key={`cust-${cust.customerId}`} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                          <td className="px-4 py-2">{cust.customerName}</td>
                          <td className="text-right px-4 py-2">{formatCurrency(cust.buckets.current)}</td>
                          <td className="text-right px-4 py-2">{formatCurrency(cust.buckets['1-30'])}</td>
                          <td className="text-right px-4 py-2">{formatCurrency(cust.buckets['31-60'])}</td>
                          <td className="text-right px-4 py-2">{formatCurrency(cust.buckets['61-90'])}</td>
                          <td className="text-right px-4 py-2">{formatCurrency(cust.buckets['91+'])}</td>
                          <td className="text-right px-4 py-2 font-bold">{formatCurrency(cust.buckets.total)}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 border-t-2 border-b-2 border-gray-900 font-bold">
                        <td className="px-4 py-2">TOTAL</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals.current)}</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals['1-30'])}</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals['31-60'])}</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals['61-90'])}</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals['91+'])}</td>
                        <td className="text-right px-4 py-2">{formatCurrency(totals.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {/* Detail View */}
                <div className="space-y-6">
                  {summary.map(cust => (
                    <div key={`detail-${cust.customerId}`} className="border border-gray-300 rounded p-4">
                      <button
                        onClick={() => toggleCustomerDetail(cust.customerId)}
                        className="flex items-center justify-between w-full text-left font-bold text-gray-900 mb-2"
                      >
                        <span>{cust.customerName}</span>
                        <span className="text-sm">{expandedCustomers[cust.customerId] ? '▼' : '▶'} {formatCurrency(cust.buckets.total)}</span>
                      </button>

                      {expandedCustomers[cust.customerId] && (
                        <table className="w-full border-collapse text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-300">
                              <th className="text-left px-2 py-1">Date</th>
                              <th className="text-left px-2 py-1">Invoice</th>
                              <th className="text-left px-2 py-1">Due Date</th>
                              <th className="text-right px-2 py-1">Amount</th>
                              <th className="text-right px-2 py-1">Open Balance</th>
                              <th className="text-left px-2 py-1">Bucket</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cust.invoices.map((inv, idx) => (
                              <tr key={`inv-${inv.invoiceNo}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-2 py-1">{inv.invoiceDate}</td>
                                <td className="px-2 py-1">{inv.invoiceNo}</td>
                                <td className="px-2 py-1">{inv.dueDate}</td>
                                <td className="text-right px-2 py-1">{formatCurrency(inv.amount)}</td>
                                <td className="text-right px-2 py-1">{formatCurrency(inv.openBalance)}</td>
                                <td className="px-2 py-1 font-semibold">{inv.bucket}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

          </div>
        )}

        {!loading && (!summary || summary.length === 0) && !error && (
          <div className="text-center text-gray-500 py-12">
            {agingData?.meta?.message || 'No open AR transactions found. Once invoices are posted, aging will appear here.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ARAgingReport;
